import { useEffect, useMemo, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { parseProfileValue } from '@/services/users/userProfileApi'
import { useUserProfile } from '../contexts/UserProfileContext'
import { useNetwork } from '../contexts/NetworkContext'

/**
 * Profile key shared with the old UI, which calls tagged objects "starred".
 * The value maps network id → object type → ids, oldest first; a line item
 * starred there shows as tagged here and the other way round.
 */
export const TAGGED_KEY = 'adnStarred'
/**
 * When each line item was tagged, by network id then line item id. Kept apart
 * from `adnStarred` because the old UI expects plain id lists there and saves
 * that key back from its own copy, which would drop anything added to it.
 */
export const TAGGED_AT_KEY = 'ruiTaggedAt'
const LINE_ITEM_TYPE = 'LineItem'

/** How long a tag lasts before it is removed by itself. */
export const TAG_LIFETIME_DAYS = 180
const TAG_LIFETIME_MS = TAG_LIFETIME_DAYS * 24 * 60 * 60 * 1000

/** Ids by network id, then by object type (`LineItem`, `Creative`, …). */
type Starred = Record<string, Record<string, string[]>>
/** ISO times by network id, then by line item id. */
type TaggedAt = Record<string, Record<string, string>>

interface TagState {
  starred: Starred
  taggedAt: TaggedAt
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)

const stateOf = (profile: Record<string, string>): TagState => {
  const starred = parseProfileValue<unknown>(profile[TAGGED_KEY], undefined)
  const taggedAt = parseProfileValue<unknown>(profile[TAGGED_AT_KEY], undefined)
  return {
    starred: isRecord(starred) ? (starred as Starred) : {},
    taggedAt: isRecord(taggedAt) ? (taggedAt as TaggedAt) : {},
  }
}

/** The profile values a tag state is stored as. */
const profileValues = (state: TagState) => ({
  [TAGGED_KEY]: state.starred,
  [TAGGED_AT_KEY]: state.taggedAt,
})

/** Each id once — the old UI can leave one in its list twice. */
const lineItemIds = (starred: Starred, networkId: string): string[] => {
  const ids = starred[networkId]?.[LINE_ITEM_TYPE]
  return Array.isArray(ids) ? [...new Set(ids)] : []
}

/** `state` with one line item tagged or untagged, every other entry left as it is. */
const withTag = (
  state: TagState,
  networkId: string,
  id: string,
  tagged: boolean,
  now: number
): TagState => {
  const others = lineItemIds(state.starred, networkId).filter((x) => x !== id)
  const times = { ...state.taggedAt[networkId] }
  if (tagged) times[id] = new Date(now).toISOString()
  else delete times[id]
  return {
    starred: {
      ...state.starred,
      [networkId]: { ...state.starred[networkId], [LINE_ITEM_TYPE]: tagged ? [...others, id] : others },
    },
    taggedAt: { ...state.taggedAt, [networkId]: times },
  }
}

/**
 * `state` with tags past their lifetime removed, in every network. A tag with
 * no time — set before times were kept, or in the old UI — counts as set now.
 * Times for line items no longer tagged are dropped. `changed` says whether
 * anything differs from `state`.
 */
const swept = (state: TagState, now: number): { state: TagState; changed: boolean } => {
  const starred: Starred = { ...state.starred }
  const taggedAt: TaggedAt = {}
  let changed = false

  for (const networkId of Object.keys(state.starred)) {
    const ids = state.starred[networkId]?.[LINE_ITEM_TYPE]
    if (!Array.isArray(ids)) continue
    const times = state.taggedAt[networkId] ?? {}
    const kept: string[] = []
    const keptTimes: Record<string, string> = {}
    for (const id of new Set(ids)) {
      const at = Date.parse(times[id] ?? '')
      if (Number.isNaN(at)) {
        keptTimes[id] = new Date(now).toISOString()
        kept.push(id)
        changed = true
      } else if (now - at > TAG_LIFETIME_MS) {
        changed = true
      } else {
        keptTimes[id] = times[id]
        kept.push(id)
      }
    }
    if (kept.length !== ids.length) {
      starred[networkId] = { ...state.starred[networkId], [LINE_ITEM_TYPE]: kept }
      changed = true
    }
    if (Object.keys(keptTimes).length > 0) taggedAt[networkId] = keptTimes
  }

  // Short of a stamp, every time kept was already there, so a lower count
  // means some belonged to line items no longer tagged.
  const timeCount = (t: TaggedAt) =>
    Object.values(t).reduce((n, times) => n + Object.keys(isRecord(times) ? times : {}).length, 0)
  if (timeCount(state.taggedAt) !== timeCount(taggedAt)) changed = true

  return { state: { starred, taggedAt }, changed }
}

/**
 * The line items the user has tagged in the current network, kept in the user
 * profile so they follow the user between browsers. Newest tag first. A tag is
 * removed by itself once it is older than `TAG_LIFETIME_DAYS`.
 */
export function useTaggedLineItems() {
  const { currentNetwork } = useNetwork()
  const networkId = currentNetwork?.id ?? ''
  const { profile, isLoading, latest, preview, save } = useUserProfile()

  // Shown at once, then saved. Every write starts from what the server has
  // now, so a tag set in another tab or in the old UI is not lost; the scope
  // runs writes one after another so quick toggles do not race. Each write
  // also clears out expired tags, and is skipped when nothing would change.
  const write = useMutation({
    scope: { id: TAGGED_KEY },
    mutationFn: async (change?: { id: string; tagged: boolean }) => {
      const now = Date.now()
      const fresh = stateOf(await latest())
      let next = fresh
      if (change) {
        if (!networkId) throw new Error('No network')
        next = withTag(fresh, networkId, change.id, change.tagged, now)
      }
      const sweep = swept(next, now)
      if (!change && !sweep.changed) return
      await save(profileValues(sweep.state))
    },
    onMutate: (change) => ({
      undo: change
        ? preview((current) =>
            profileValues(withTag(stateOf(current), networkId, change.id, change.tagged, Date.now()))
          )
        : undefined,
    }),
    onError: (_error, _vars, ctx) => ctx?.undo?.(),
  })

  // Expired tags are hidden at once and cleared from the profile once per
  // load, so they also leave the old UI.
  const current = useMemo(
    () => (profile ? swept(stateOf(profile), Date.now()) : undefined),
    [profile]
  )
  const sweptOnce = useRef(false)
  const { mutate } = write
  useEffect(() => {
    if (!current?.changed || sweptOnce.current) return
    sweptOnce.current = true
    mutate(undefined)
  }, [current, mutate])

  const ids = [...lineItemIds(current?.state.starred ?? {}, networkId)].reverse()

  return {
    ids,
    isLoading: isLoading || !networkId,
    isTagged: (id: string) => ids.includes(id),
    setTagged: (id: string, tagged: boolean) => write.mutateAsync({ id, tagged }),
  }
}
